import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useVehicles } from '@/hooks/useVehicles'
import { useReservations } from '@/hooks/useReservations'
import { lumi } from '@/lib/lumi'
import {ChevronLeft, Save, Car, User, Calendar, CreditCard, FileText, Loader2} from 'lucide-react'

const STATUS_OPTIONS = [
  { value: 'pending', label: 'En attente', color: 'bg-yellow-400 text-black' },
  { value: 'confirmed', label: 'Confirmee', color: 'bg-blue-500 text-white' },
  { value: 'paid', label: 'Payee', color: 'bg-green-500 text-white' },
  { value: 'active', label: 'En cours', color: 'bg-purple-500 text-white' },
  { value: 'completed', label: 'Terminee', color: 'bg-gray-500 text-white' },
  { value: 'cancelled', label: 'Annulee', color: 'bg-red-500 text-white' },
]

const PERIOD_OPTIONS = [
  { value: 'half-day-am', label: 'Demi-journee matin (8h - 12h)' },
  { value: 'half-day-pm', label: 'Demi-journee apres-midi (13h - 17h)' },
  { value: 'full-day', label: 'Journee complete (8h - 17h)' },
]

export default function ReservationForm() {
  const { id } = useParams()
  const isEditing = Boolean(id)
  const navigate = useNavigate()
  const { user, isAdmin } = useAuth()
  const { vehicles, fetchVehicles } = useVehicles()
  const { createReservation, updateReservation } = useReservations()

  const [loading, setLoading] = useState(false)
  const [loadingData, setLoadingData] = useState(false)
  const [saving, setSaving] = useState(false)

  const [form, setForm] = useState({
    vehicleId: '',
    vehicleName: '',
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    clientAddress: '',
    startDate: '',
    period: 'full-day',
    numberOfDays: 1,
    pickupLocation: 'Rue de l\'Industrie 49, 1030 Bussigny',
    returnLocation: 'Rue de l\'Industrie 49, 1030 Bussigny',
    status: 'confirmed',
    paymentStatus: 'unpaid',
    paymentMethod: '',
    deposit: 0,
    depositStatus: 'pending',
    notes: '',
    totalPrice: 0,
    basePrice: 0,
  })

  useEffect(() => {
    if (!isAdmin) {
      navigate('/')
      return
    }
    fetchVehicles()
    if (isEditing && id) {
      loadReservation(id)
    }
  }, [isAdmin, id])

  const loadReservation = async (reservationId: string) => {
    setLoadingData(true)
    try {
      const { list } = await lumi.entities.reservations.list({
        filter: { _id: reservationId }
      })
      if (list.length > 0) {
        const r = list[0]
        const startDateObj = new Date(r.startDate)
        const endDateObj = new Date(r.endDate)
        const startHour = startDateObj.getUTCHours()
        const endHour = endDateObj.getUTCHours()

        // Determiner la periode
        const sameDay = startDateObj.toISOString().slice(0, 10) === endDateObj.toISOString().slice(0, 10)
        let period = 'full-day'
        let numberOfDays = 1
        if (sameDay && endHour <= 12) {
          period = 'half-day-am'
        } else if (sameDay && startHour >= 13 && endHour <= 17) {
          period = 'half-day-pm'
        } else {
          const diffTime = endDateObj.getTime() - startDateObj.getTime()
          numberOfDays = Math.max(1, Math.ceil(diffTime / (1000 * 60 * 60 * 24)))
          if (!sameDay && endHour <= 12) {
            numberOfDays = Math.max(1, numberOfDays)
          }
        }

        setForm({
          vehicleId: r.vehicleId || '',
          vehicleName: r.vehicleName || '',
          clientName: r.clientName || '',
          clientEmail: r.clientEmail || '',
          clientPhone: r.clientPhone || '',
          clientAddress: r.clientAddress || '',
          startDate: startDateObj.toISOString().slice(0, 10),
          period,
          numberOfDays,
          pickupLocation: r.pickupLocation || 'Rue de l\'Industrie 49, 1030 Bussigny',
          returnLocation: r.returnLocation || 'Rue de l\'Industrie 49, 1030 Bussigny',
          status: r.status || 'pending',
          paymentStatus: r.paymentStatus || 'unpaid',
          paymentMethod: r.paymentMethod || '',
          deposit: r.deposit || 0,
          depositStatus: r.depositStatus || 'pending',
          notes: r.notes || '',
          totalPrice: r.totalPrice || 0,
          basePrice: r.basePrice || 0,
        })
      }
    } catch (error) {
      console.error('Erreur chargement reservation:', error)
      alert('Erreur lors du chargement de la reservation')
    } finally {
      setLoadingData(false)
    }
  }

  // Calcul automatique du prix
  useEffect(() => {
    if (!form.vehicleId || !form.startDate) return
    const selectedVehicle = vehicles.find((v: any) => v._id === form.vehicleId)
    if (!selectedVehicle) return

    const pricePerDay = selectedVehicle.pricePerDay || 0
    const pricePerHalfDay = selectedVehicle.pricePerHalfDay || Math.round(pricePerDay / 2)
    let basePrice = 0

    if (form.period === 'half-day-am') {
      basePrice = pricePerHalfDay
    } else if (form.period === 'half-day-pm') {
      basePrice = pricePerHalfDay
    } else {
      // Pour une journée complète : compter TOUS les jours calendaires (dimanches inclus = jours payants)
      // + 1 jour supplémentaire si le retour tombe un dimanche (reporté au lundi = jour payant de plus)
      const start = new Date(form.startDate + 'T00:00:00')
      const lastDay = new Date(start)
      lastDay.setDate(lastDay.getDate() + form.numberOfDays - 1)
      const extraMonday = lastDay.getDay() === 0 ? 1 : 0
      const billedDays = form.numberOfDays + extraMonday
      basePrice = pricePerDay * billedDays
    }

    setForm(prev => ({
      ...prev,
      basePrice: Math.round(basePrice * 100) / 100,
      totalPrice: Math.round(basePrice * 100) / 100,
    }))
  }, [form.vehicleId, form.period, form.numberOfDays, form.startDate, vehicles])

  // Nombre de dimanches inclus + détection lundi supplémentaire (retour dimanche reporté au lundi)
  const { sundaysIncluded, extraMondayDay } = (() => {
    if (!form.startDate || form.period !== 'full-day' || form.numberOfDays < 1) {
      return { sundaysIncluded: 0, extraMondayDay: 0 }
    }
    let count = 0
    const start = new Date(form.startDate + 'T00:00:00')
    for (let i = 0; i < form.numberOfDays; i++) {
      const d = new Date(start)
      d.setDate(d.getDate() + i)
      if (d.getDay() === 0) count++
    }
    const lastDay = new Date(start)
    lastDay.setDate(lastDay.getDate() + form.numberOfDays - 1)
    const extra = lastDay.getDay() === 0 ? 1 : 0
    return { sundaysIncluded: count, extraMondayDay: extra }
  })()

  const handleVehicleChange = (vehicleId: string) => {
    const vehicle = vehicles.find((v: any) => v._id === vehicleId)
    setForm(prev => ({
      ...prev,
      vehicleId,
      vehicleName: vehicle ? `${vehicle.brand} ${vehicle.model}` : '',
      deposit: vehicle?.deposit || 0,
    }))
  }

  const computeDates = () => {
    const startDateStr = form.startDate
    if (!startDateStr) return { startDate: '', endDate: '' }

    const startDate = form.period === 'half-day-pm'
      ? new Date(startDateStr + 'T13:00:00.000Z')
      : new Date(startDateStr + 'T08:00:00.000Z')

    let endDate: Date
    if (form.period === 'half-day-am') {
      endDate = new Date(startDateStr + 'T12:00:00.000Z')
    } else if (form.period === 'half-day-pm') {
      endDate = new Date(startDateStr + 'T17:00:00.000Z')
    } else {
      const end = new Date(startDate)
      end.setUTCDate(end.getUTCDate() + form.numberOfDays - 1)
      const endDatePart = end.toISOString().slice(0, 10)
      endDate = new Date(endDatePart + 'T17:00:00.000Z')
    }

    // Si le retour tombe un dimanche, on decale au lundi suivant (sans surcout)
    if (endDate.getUTCDay() === 0) {
      endDate = new Date(endDate)
      endDate.setUTCDate(endDate.getUTCDate() + 1)
    }

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    }
  }

  // Regle metier: interdire samedi apres-midi si on est deja samedi apres 11h
  const isSaturdayAfternoonBlocked = (() => {
    if (!form.startDate || form.period !== 'half-day-pm') return false
    const selected = new Date(form.startDate + 'T00:00:00')
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    if (selected.getTime() !== today.getTime()) return false
    if (selected.getDay() !== 6) return false
    return now.getHours() >= 11
  })()

  // Regle metier: le dimanche, le garage est ferme (pas de prise ni de retour possible)
  const isPickupSunday = (() => {
    if (!form.startDate) return false
    const selected = new Date(form.startDate + 'T00:00:00')
    return selected.getDay() === 0
  })()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!form.vehicleId || !form.clientName || !form.clientPhone || !form.startDate) {
      alert('Veuillez remplir tous les champs obligatoires')
      return
    }

    if (isPickupSunday) {
      alert('Le garage est ferme le dimanche : la prise du vehicule n\'est pas possible ce jour-la. Le dimanche reste compte comme jour paye en cours de location.')
      return
    }

    if (isSaturdayAfternoonBlocked) {
      alert('Reservation du samedi apres-midi impossible apres 11h00 le jour meme.')
      return
    }

    setSaving(true)
    try {
      const { startDate, endDate } = computeDates()
      const now = new Date().toISOString()

      const reservationData: any = {
        vehicleId: form.vehicleId,
        vehicleName: form.vehicleName,
        clientName: form.clientName,
        clientEmail: form.clientEmail,
        clientPhone: form.clientPhone,
        clientAddress: form.clientAddress,
        startDate,
        endDate,
        pickupLocation: form.pickupLocation,
        returnLocation: form.returnLocation,
        status: form.status,
        paymentStatus: form.paymentStatus,
        paymentMethod: form.paymentMethod,
        deposit: form.deposit,
        depositStatus: form.depositStatus,
        notes: form.notes,
        totalPrice: form.totalPrice,
        basePrice: form.basePrice,
        optionsPrice: 0,
        updatedAt: now,
      }

      if (isEditing && id) {
        await updateReservation(id, reservationData)
        alert('Reservation mise a jour avec succes !')
      } else {
        reservationData.reservationNumber = 'RES-' + Date.now().toString(36).toUpperCase()
        reservationData.creator = user?.userId || 'admin'
        reservationData.creatorEmail = user?.email || ''
        reservationData.createdAt = now
        await createReservation(reservationData)
        alert('Reservation creee avec succes !')
      }

      navigate('/admin')
    } catch (error) {
      console.error('Erreur sauvegarde:', error)
      alert('Erreur lors de la sauvegarde de la reservation')
    } finally {
      setSaving(false)
    }
  }

  if (!isAdmin) return null

  if (loadingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-yellow-400 animate-spin mx-auto mb-4" />
          <p className="text-xl text-white">Chargement de la reservation...</p>
        </div>
      </div>
    )
  }

  const selectedVehicle = vehicles.find((v: any) => v._id === form.vehicleId)

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Header */}
      <div className="bg-black text-white py-8 border-b border-yellow-400/20 shadow-2xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 text-yellow-400 hover:text-yellow-300 mb-4 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            Retour a l'administration
          </button>
          <h1 className="text-3xl font-black bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-400 bg-clip-text text-transparent">
            {isEditing ? 'Modifier la reservation' : 'Nouvelle reservation manuelle'}
          </h1>
          <p className="text-gray-400 mt-1">
            {isEditing ? 'Modifiez les informations de la reservation' : 'Ajoutez une reservation pour un client venu au garage'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Section Vehicule */}
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 border-2 border-gray-700">
          <h2 className="text-xl font-black text-white mb-6 flex items-center gap-3">
            <div className="bg-yellow-400 p-2 rounded-lg">
              <Car className="w-5 h-5 text-black" />
            </div>
            Vehicule
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-gray-300 mb-2">
                Vehicule <span className="text-red-400">*</span>
              </label>
              <select
                value={form.vehicleId}
                onChange={(e) => handleVehicleChange(e.target.value)}
                className="w-full bg-gray-700 border-2 border-gray-600 rounded-xl px-4 py-3 text-white focus:border-yellow-400 focus:outline-none transition-colors"
                required
              >
                <option value="">Selectionnez un vehicule</option>
                {vehicles.map((v: any) => (
                  <option key={v._id} value={v._id}>
                    {v.brand} {v.model} - {v.name} (CHF {v.pricePerHalfDay || Math.round((v.pricePerDay || 0) / 2)}/½j — CHF {v.pricePerDay}/j)
                  </option>
                ))}
              </select>
            </div>
            {selectedVehicle && (
              <div className="flex items-center gap-4 bg-gray-700/50 rounded-xl p-4 border border-gray-600">
                {selectedVehicle.imageUrl && (
                  <img
                    src={selectedVehicle.imageUrl}
                    alt={selectedVehicle.name}
                    className="w-20 h-14 object-cover rounded-lg"
                  />
                )}
                <div>
                  <p className="font-bold text-white">{selectedVehicle.brand} {selectedVehicle.model}</p>
                  <p className="text-yellow-400 font-bold">CHF {selectedVehicle.pricePerHalfDay || Math.round((selectedVehicle.pricePerDay || 0) / 2)}/½j — CHF {selectedVehicle.pricePerDay}/jour</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Section Client */}
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 border-2 border-gray-700">
          <h2 className="text-xl font-black text-white mb-6 flex items-center gap-3">
            <div className="bg-blue-500 p-2 rounded-lg">
              <User className="w-5 h-5 text-white" />
            </div>
            Informations client
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-gray-300 mb-2">
                Nom complet <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={form.clientName}
                onChange={(e) => setForm(prev => ({ ...prev, clientName: e.target.value }))}
                className="w-full bg-gray-700 border-2 border-gray-600 rounded-xl px-4 py-3 text-white focus:border-yellow-400 focus:outline-none transition-colors"
                placeholder="Jean Dupont"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-300 mb-2">
                Telephone <span className="text-red-400">*</span>
              </label>
              <input
                type="tel"
                value={form.clientPhone}
                onChange={(e) => setForm(prev => ({ ...prev, clientPhone: e.target.value }))}
                className="w-full bg-gray-700 border-2 border-gray-600 rounded-xl px-4 py-3 text-white focus:border-yellow-400 focus:outline-none transition-colors"
                placeholder="+41 79 123 45 67"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-300 mb-2">Email</label>
              <input
                type="email"
                value={form.clientEmail}
                onChange={(e) => setForm(prev => ({ ...prev, clientEmail: e.target.value }))}
                className="w-full bg-gray-700 border-2 border-gray-600 rounded-xl px-4 py-3 text-white focus:border-yellow-400 focus:outline-none transition-colors"
                placeholder="jean@exemple.ch"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-300 mb-2">Adresse</label>
              <input
                type="text"
                value={form.clientAddress}
                onChange={(e) => setForm(prev => ({ ...prev, clientAddress: e.target.value }))}
                className="w-full bg-gray-700 border-2 border-gray-600 rounded-xl px-4 py-3 text-white focus:border-yellow-400 focus:outline-none transition-colors"
                placeholder="Rue de Lausanne 10, 1000 Lausanne"
              />
            </div>
          </div>
        </div>

        {/* Section Dates & Periode */}
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 border-2 border-gray-700">
          <h2 className="text-xl font-black text-white mb-6 flex items-center gap-3">
            <div className="bg-purple-500 p-2 rounded-lg">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            Dates et periode
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-bold text-gray-300 mb-2">
                Date de debut <span className="text-red-400">*</span>
              </label>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => setForm(prev => ({ ...prev, startDate: e.target.value }))}
                className="w-full bg-gray-700 border-2 border-gray-600 rounded-xl px-4 py-3 text-white focus:border-yellow-400 focus:outline-none transition-colors"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-300 mb-2">Type de periode</label>
              <select
                value={form.period}
                onChange={(e) => setForm(prev => ({ ...prev, period: e.target.value }))}
                className="w-full bg-gray-700 border-2 border-gray-600 rounded-xl px-4 py-3 text-white focus:border-yellow-400 focus:outline-none transition-colors"
              >
                {PERIOD_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            {form.period === 'full-day' && (
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Nombre de jours</label>
                <input
                  type="number"
                  min={1}
                  max={90}
                  value={form.numberOfDays}
                  onChange={(e) => setForm(prev => ({ ...prev, numberOfDays: Math.max(1, parseInt(e.target.value) || 1) }))}
                  className="w-full bg-gray-700 border-2 border-gray-600 rounded-xl px-4 py-3 text-white focus:border-yellow-400 focus:outline-none transition-colors"
                />
              </div>
            )}
          </div>

          {/* Resume des dates */}
          {form.startDate && (
            <div className="mt-6 bg-gray-700/50 rounded-xl p-4 border border-gray-600 space-y-2">
              <div>
                <p className="text-sm text-gray-400 mb-1">Resume</p>
                <p className="text-white font-bold">
                  {form.period === 'half-day-am' || form.period === 'half-day-pm' ? (
                    <>
                      {new Date(form.startDate).toLocaleDateString('fr-CH', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                      {' '} — {form.period === 'half-day-am' ? 'Demi-journee matin (8h00 - 12h00)' : 'Demi-journee apres-midi (13h00 - 17h00)'}
                    </>
                  ) : (
                    <>
                      Du {new Date(form.startDate).toLocaleDateString('fr-CH', { weekday: 'long', day: 'numeric', month: 'long' })} (8h00)
                      {' au '}
                      {(() => {
                        const end = new Date(form.startDate)
                        end.setDate(end.getDate() + form.numberOfDays - 1)
                        return end.toLocaleDateString('fr-CH', { weekday: 'long', day: 'numeric', month: 'long' })
                      })()}
                      {' '}(17h00) — {form.numberOfDays} jour{form.numberOfDays > 1 ? 's' : ''}
                    </>
                  )}
                </p>
              </div>

              {/* Info retour dimanche decale au lundi */}
              {(() => {
                let theoreticalEnd: Date
                if (form.period === 'half-day-am') {
                  theoreticalEnd = new Date(form.startDate + 'T12:00:00')
                } else if (form.period === 'half-day-pm') {
                  theoreticalEnd = new Date(form.startDate + 'T17:00:00')
                } else {
                  const end = new Date(form.startDate)
                  end.setDate(end.getDate() + form.numberOfDays - 1)
                  theoreticalEnd = end
                }
                if (theoreticalEnd.getDay() === 0) {
                  return (
                    <div className="bg-blue-500/10 border border-blue-500/40 rounded-lg px-3 py-2 text-blue-300 text-sm flex items-start gap-2">
                      <Calendar className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>Le retour tombe un dimanche : il sera automatiquement reporte au lundi suivant (sans surcout).</span>
                    </div>
                  )
                }
                return null
              })()}

              {/* Alerte dimanche garage ferme */}
              {isPickupSunday && (
                <div className="bg-orange-500/10 border border-orange-500/40 rounded-lg px-3 py-2 text-orange-300 text-sm flex items-start gap-2">
                  <Calendar className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Dimanche : garage ferme, pas de prise ni de retour possible. Le dimanche reste toutefois compte comme jour paye lorsqu'il est inclus dans une location en cours.</span>
                </div>
              )}

              {/* Alerte samedi apres-midi bloque */}
              {isSaturdayAfternoonBlocked && (
                <div className="bg-red-500/10 border border-red-500/40 rounded-lg px-3 py-2 text-red-300 text-sm flex items-start gap-2">
                  <Calendar className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Reservation du samedi apres-midi impossible apres 11h00 le jour meme.</span>
                </div>
              )}

              {/* Info dimanches inclus = jours payants */}
              {sundaysIncluded > 0 && (
                <div className="bg-yellow-500/10 border border-yellow-500/40 rounded-lg px-3 py-2 text-yellow-300 text-sm flex items-start gap-2">
                  <Calendar className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    {sundaysIncluded} dimanche{sundaysIncluded > 1 ? 's' : ''} inclus dans la période : compté{sundaysIncluded > 1 ? 's' : ''} comme jour{sundaysIncluded > 1 ? 's' : ''} payant{sundaysIncluded > 1 ? 's' : ''} (garage fermé mais véhicule conservé par le client).
                  </span>
                </div>
              )}

              {/* Info lundi supplémentaire facturé si retour dimanche reporté */}
              {extraMondayDay > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg px-3 py-2 text-amber-300 text-sm flex items-start gap-2">
                  <Calendar className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    +1 jour facturé : le retour tombe un dimanche et est reporté au lundi → ce lundi supplémentaire est compté comme jour payant (durée totale facturée : {form.numberOfDays + extraMondayDay} jours).
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Lieux */}
          <div className="grid md:grid-cols-2 gap-6 mt-6">
            <div>
              <label className="block text-sm font-bold text-gray-300 mb-2">Lieu de retrait</label>
              <input
                type="text"
                value={form.pickupLocation}
                onChange={(e) => setForm(prev => ({ ...prev, pickupLocation: e.target.value }))}
                className="w-full bg-gray-700 border-2 border-gray-600 rounded-xl px-4 py-3 text-white focus:border-yellow-400 focus:outline-none transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-300 mb-2">Lieu de retour</label>
              <input
                type="text"
                value={form.returnLocation}
                onChange={(e) => setForm(prev => ({ ...prev, returnLocation: e.target.value }))}
                className="w-full bg-gray-700 border-2 border-gray-600 rounded-xl px-4 py-3 text-white focus:border-yellow-400 focus:outline-none transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Section Statut & Paiement */}
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 border-2 border-gray-700">
          <h2 className="text-xl font-black text-white mb-6 flex items-center gap-3">
            <div className="bg-green-500 p-2 rounded-lg">
              <CreditCard className="w-5 h-5 text-white" />
            </div>
            Statut et paiement
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-gray-300 mb-2">Statut de la reservation</label>
              <div className="grid grid-cols-2 gap-2">
                {STATUS_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, status: opt.value }))}
                    className={`px-4 py-2.5 rounded-xl text-sm font-bold transition-all ${
                      form.status === opt.value
                        ? `${opt.color} ring-2 ring-white shadow-lg scale-105`
                        : 'bg-gray-700 text-gray-400 hover:bg-gray-600 hover:text-white'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Statut du paiement</label>
                <select
                  value={form.paymentStatus}
                  onChange={(e) => setForm(prev => ({ ...prev, paymentStatus: e.target.value }))}
                  className="w-full bg-gray-700 border-2 border-gray-600 rounded-xl px-4 py-3 text-white focus:border-yellow-400 focus:outline-none transition-colors"
                >
                  <option value="unpaid">Non paye</option>
                  <option value="partial">Paiement partiel</option>
                  <option value="paid">Paye</option>
                  <option value="refunded">Rembourse</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-300 mb-2">Methode de paiement</label>
                <select
                  value={form.paymentMethod}
                  onChange={(e) => setForm(prev => ({ ...prev, paymentMethod: e.target.value }))}
                  className="w-full bg-gray-700 border-2 border-gray-600 rounded-xl px-4 py-3 text-white focus:border-yellow-400 focus:outline-none transition-colors"
                >
                  <option value="">Non defini</option>
                  <option value="cash">Especes</option>
                  <option value="card">Carte bancaire</option>
                  <option value="twint">TWINT</option>
                  <option value="transfer">Virement bancaire</option>
                </select>
              </div>
            </div>
          </div>

          {/* Caution */}
          <div className="grid md:grid-cols-2 gap-6 mt-6">
            <div>
              <label className="block text-sm font-bold text-gray-300 mb-2">Caution (CHF)</label>
              <input
                type="number"
                min={0}
                step={50}
                value={form.deposit}
                onChange={(e) => setForm(prev => ({ ...prev, deposit: parseFloat(e.target.value) || 0 }))}
                className="w-full bg-gray-700 border-2 border-gray-600 rounded-xl px-4 py-3 text-white focus:border-yellow-400 focus:outline-none transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-300 mb-2">Statut de la caution</label>
              <select
                value={form.depositStatus}
                onChange={(e) => setForm(prev => ({ ...prev, depositStatus: e.target.value }))}
                className="w-full bg-gray-700 border-2 border-gray-600 rounded-xl px-4 py-3 text-white focus:border-yellow-400 focus:outline-none transition-colors"
              >
                <option value="pending">En attente</option>
                <option value="paid">Encaissee</option>
                <option value="refunded">Remboursee</option>
              </select>
            </div>
          </div>
        </div>

        {/* Section Prix */}
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 border-2 border-yellow-400/30">
          <h2 className="text-xl font-black text-white mb-6 flex items-center gap-3">
            <div className="bg-yellow-400 p-2 rounded-lg">
              <CreditCard className="w-5 h-5 text-black" />
            </div>
            Tarification
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-gray-300 mb-2">Prix de base (CHF)</label>
              <input
                type="number"
                min={0}
                step={0.5}
                value={form.basePrice}
                onChange={(e) => {
                  const base = parseFloat(e.target.value) || 0
                  setForm(prev => ({ ...prev, basePrice: base, totalPrice: base }))
                }}
                className="w-full bg-gray-700 border-2 border-gray-600 rounded-xl px-4 py-3 text-white focus:border-yellow-400 focus:outline-none transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-300 mb-2">Prix total (CHF)</label>
              <input
                type="number"
                min={0}
                step={0.5}
                value={form.totalPrice}
                onChange={(e) => setForm(prev => ({ ...prev, totalPrice: parseFloat(e.target.value) || 0 }))}
                className="w-full bg-gray-700 border-2 border-yellow-400 rounded-xl px-4 py-3 text-white text-2xl font-black focus:border-yellow-300 focus:outline-none transition-colors"
              />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-6 border-2 border-gray-700">
          <h2 className="text-xl font-black text-white mb-6 flex items-center gap-3">
            <div className="bg-gray-500 p-2 rounded-lg">
              <FileText className="w-5 h-5 text-white" />
            </div>
            Notes internes
          </h2>
          <textarea
            value={form.notes}
            onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
            rows={4}
            className="w-full bg-gray-700 border-2 border-gray-600 rounded-xl px-4 py-3 text-white focus:border-yellow-400 focus:outline-none transition-colors resize-none"
            placeholder="Notes internes (non visibles par le client)..."
          />
        </div>

        {/* Actions */}
        <div className="flex gap-4 justify-end pb-8">
          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="px-8 py-4 bg-gray-700 text-white rounded-xl font-bold hover:bg-gray-600 transition-all"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black rounded-xl font-black hover:shadow-2xl hover:shadow-yellow-400/50 transition-all hover:scale-105 disabled:opacity-50 disabled:hover:scale-100"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                {isEditing ? 'Mettre a jour' : 'Creer la reservation'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}